import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import LastMinuteSummary from './LastMinuteSummary'
import OfferBNPLAd from '../OfferBNPLAd'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import BodyText from 'components/Luxkit/Typography/BodyText'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import LastMinutePropertyDetails from './LastMinutePropertyDetails'
import useShareModalConfig from 'hooks/useShareModalConfig'
import OfferPageLabelGroup from '../OfferPageLabelGroup'

const OfferIntroGrid = styled(LayoutContainer)`
  display: grid;
  padding: ${rem(20)};
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    padding: ${rem(20)} ${rem(40)};
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(30)} ${rem(12)};
    grid-gap: ${rem(16)} ${rem(40)};
    grid-template-columns: 1fr min-content;
    grid-template-rows: min-content min-content min-content min-content 1fr;
  }
`

const OfferBadgeIntroWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-row: 4;

  ${mediaQueryUp.tablet} {
    grid-row: 3;
    grid-column: 1;
  }
`

const OfferBadgeTagSection = styled(BodyText)`

  > * {
    margin: 0;
  }
`

const BNPLAd = styled(OfferBNPLAd)`
  margin-top: ${rem(24)};
  padding-top: ${rem(24)};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const SummaryBlock = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    position: sticky;
    /* height of the filter bar */
    top: ${rem(64)};
    display: block;
    align-self: flex-start;
    grid-row: 3;
    grid-column: 2;
  }

  ${mediaQueryUp.desktop} {
    grid-row: 2 / span 3;
    grid-column: 2;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const HotelSummary = styled(LastMinuteSummary)`
  display: block;
  align-self: flex-start;
  min-width: ${rem(290)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(370)};
  }
`

const OfferOtherActions = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  grid-row: 3;
  grid-column: 1;

  ${mediaQueryUp.tablet} {
    justify-content: flex-end;
    grid-row: 1;
    grid-column: 2;
  }
`

interface Props {
  offer: App.Offer;
  className?: string;
  onViewPackageOptions: () => void;
  flightDestination?: App.Airport;
  queryString?: string;
}

function LastMinuteIntro(props: Props) {
  const {
    offer,
    className,
    onViewPackageOptions,
    flightDestination,
    queryString,
  } = props

  const property = offer.property
  const location = [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')

  const shareConfig = useShareModalConfig({
    image: offer.images[0],
    offerId: offer.id,
    location,
    type: 'offer',
    utmCampaign: 'webshare',
    queryString,
    offerType: 'hotel',
  })

  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])

  return (
    <OfferIntroGrid className={className} data-testid="offer-intro">
      <OfferPageLabelGroup offer={offer} />
      <OfferOtherActions>
        <TripGuard inverse>
          <ShareModal shareConfig={shareConfig}>
            <ShareWithFriendsButton horizontalOutdent="end" />
          </ShareModal>
        </TripGuard>
        <TripGuard>
          <ShareModal shareConfig={shareConfig}>
            <ShareWithFriendsButton />
          </ShareModal>
          <BookmarkButton offer={offer} />
        </TripGuard>
      </OfferOtherActions>

      <OfferBadgeIntroWrapper>
        {offer.badge && <OfferBadgeTagSection variant="medium">
          <OfferBadgeLabel badge={offer.badge} />
        </OfferBadgeTagSection>}
        <LastMinutePropertyDetails
          offer={offer}
          property={property}
          location={location}
        />
      </OfferBadgeIntroWrapper>
      <SummaryBlock>
        <div>
          <HotelSummary onViewPackageOptions={onViewPackageOptions} flightDestination={flightDestination} />
        </div>
        <OfferBNPLAd prices={allPrices} />
      </SummaryBlock>
      <BNPLAd prices={allPrices} />
    </OfferIntroGrid>
  )
}

export default withOfferPageAndReduxState((state) => ({
  offer: state.offer,
}), LastMinuteIntro)
